import { useState, useEffect, useCallback, useRef } from "react";

// ── Types ────────────────────────────────────────────────
type StatsData = {
  users: {
    total: number; this_month: number; this_week: number; today: number;
    google_login: number; free_plan: number; paid_plan: number;
    monthly_chart: { month: string; count: number }[];
    error?: string;
  };
  reports: {
    capital_gain: {
      total: number; this_month: number; today: number;
      monthly_chart: { month: string; count: number }[];
    };
    bank_statement: { total: number; this_month: number; today: number };
    total_all: number;
    top_clients: { name: string; count: number }[];
    error?: string;
  };
  server: {
    cpu_percent: number; cpu_count: number;
    memory_total_gb: number; memory_used_gb: number; memory_percent: number;
    disk_total_gb: number; disk_used_gb: number; disk_percent: number; disk_free_gb: number;
    error?: string;
  };
  storage: {
    reports_bucket:  { size_gb: number; size_mb: number; count: number };
    uploads_bucket:  { size_gb: number; size_mb: number; count: number };
    textract_bucket: { size_gb: number; size_mb: number; count: number };
    total_gb: number; total_files: number;
    error?: string;
  };
  recent: {
    users:         { name: string; email: string; plan: string; joined: string }[];
    capital_gain:  { client: string; ay: string; date: string }[];
    bank_statement:{ client: string; bank: string; date: string }[];
    error?: string;
  };
  revenue: { status: string; message: string; mrr: number; total: number; this_month: number; currency: string };
  generated_at: string;
};

type UserRow = {
  id: string; name: string; email: string; plan: string;
  joined: string; login_type: string; country: string;
};

// ── Sub-components ────────────────────────────────────────

const ProgressBar = ({ label, value, max = 100, unit = "%" }: { label: string; value: number; max?: number; unit?: string }) => {
  const pct = Math.min((value / max) * 100, 100);
  const color = pct < 60 ? "#375623" : pct < 80 ? "#C9A84C" : "#9C0006";
  return (
    <div style={{ marginBottom: "16px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px", fontSize: "13px", fontWeight: 600 }}>
        <span style={{ color: "#374151" }}>{label}</span>
        <span style={{ color }}>{Math.round(value)}{unit}</span>
      </div>
      <div style={{ height: "8px", background: "#EAE6E0", borderRadius: "4px", overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${pct}%`, background: color, borderRadius: "4px", transition: "width 0.5s ease" }} />
      </div>
    </div>
  );
};

const BarChart = ({ data, label, color = "#C9A84C" }: { data: { month: string; count: number }[]; label: string; color?: string }) => {
  const max = Math.max(...data.map(d => d.count), 1);
  return (
    <div style={{ padding: "16px" }}>
      <h4 style={{ marginBottom: "16px", fontSize: "14px", fontWeight: 700, color: "#1f2937" }}>{label}</h4>
      <div style={{ display: "flex", alignItems: "flex-end", gap: "8px", height: "120px" }}>
        {data.map((d, i) => (
          <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: "4px", height: "100%", justifyContent: "flex-end" }}>
            <span style={{ fontSize: "11px", fontWeight: 600, color: "#374151" }}>{d.count}</span>
            <div style={{ width: "100%", background: color, borderRadius: "4px 4px 0 0", height: `${(d.count / max) * 100}%`, minHeight: d.count > 0 ? "4px" : "0", transition: "height 0.5s ease" }} />
            <span style={{ fontSize: "10px", color: "#9ca3af", textAlign: "center", lineHeight: 1.2 }}>{d.month}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const PlanBadge = ({ plan }: { plan: string }) => {
  const isPaid = plan && plan !== "free";
  return (
    <span style={{
      padding: "2px 8px", borderRadius: "99px", fontSize: "11px", fontWeight: 700,
      background: isPaid ? "#FFF2CC" : "#F3F4F6",
      color: isPaid ? "#7F6000" : "#6B7280",
    }}>
      {plan || "free"}
    </span>
  );
};

const StatCard = ({
  icon, title, value, subtext, color, dimmed = false,
}: { icon: string; title: string; value: string | number; subtext: string; color: string; dimmed?: boolean }) => (
  <div style={{
    background: "white", border: "1.5px solid #E5E7EB", borderRadius: "14px", padding: "20px",
    display: "flex", flexDirection: "column", gap: "8px", opacity: dimmed ? 0.6 : 1,
  }}>
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <div style={{ width: "40px", height: "40px", borderRadius: "50%", background: color + "20", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "20px" }}>
        {icon}
      </div>
    </div>
    <div style={{ fontSize: "28px", fontWeight: 800, color: "#111827", lineHeight: 1 }}>{value}</div>
    <div>
      <div style={{ fontSize: "13px", color: "#6B7280", fontWeight: 500 }}>{title}</div>
      <div style={{ fontSize: "12px", color: "#9CA3AF", marginTop: "2px" }}>{subtext}</div>
    </div>
  </div>
);

// ── Main Component ────────────────────────────────────────

export default function AdminDashboard() {
  const [stats, setStats]           = useState<StatsData | null>(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState("");
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  // Users table
  const [users, setUsers]           = useState<UserRow[]>([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersPage, setUsersPage]   = useState(1);
  const [usersTotalPages, setUsersTotalPages] = useState(1);
  const [search, setSearch]         = useState("");
  const [usersLoading, setUsersLoading] = useState(false);
  const searchTimeout               = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchStats = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) { window.location.href = "/"; return; }
    setLoading(true);
    try {
      const res = await fetch("/api/admin/stats", {
        headers: { Authorization: "Bearer " + token },
      });
      if (res.status === 403) { alert("Admin access required"); window.location.href = "/"; return; }
      if (res.status === 401) { window.location.href = "/"; return; }
      if (!res.ok) throw new Error("Failed to load stats");
      const data = await res.json();
      setStats(data);
      setLastRefresh(new Date());
      setError("");
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUsers = useCallback(async (page: number, q: string) => {
    const token = localStorage.getItem("token");
    if (!token) return;
    setUsersLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: "20", search: q });
      const res = await fetch(`/api/admin/users?${params}`, {
        headers: { Authorization: "Bearer " + token },
      });
      if (!res.ok) return;
      const data = await res.json();
      setUsers(data.users);
      setUsersTotal(data.total);
      setUsersTotalPages(data.total_pages);
    } finally {
      setUsersLoading(false);
    }
  }, []);

  // Initial load + auth check
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) { window.location.href = "/"; return; }
    fetchStats();
    fetchUsers(1, "");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh every 60 s
  useEffect(() => {
    const interval = setInterval(fetchStats, 60000);
    return () => clearInterval(interval);
  }, [fetchStats]);

  // Debounced search
  useEffect(() => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setUsersPage(1);
      fetchUsers(1, search);
    }, 300);
    return () => { if (searchTimeout.current) clearTimeout(searchTimeout.current); };
  }, [search, fetchUsers]);

  // Page change
  useEffect(() => {
    fetchUsers(usersPage, search);
  }, [usersPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("userPicture");
    window.location.href = "/";
  };

  // ── Render ──────────────────────────────────────────────

  const card: React.CSSProperties = {
    background: "white", border: "1.5px solid #E5E7EB",
    borderRadius: "14px", padding: "24px",
  };

  const sectionTitle: React.CSSProperties = {
    fontSize: "16px", fontWeight: 700, color: "#1f2937", marginBottom: "16px",
  };

  if (loading && !stats) {
    return (
      <div style={{ minHeight: "100vh", background: "#F3F4F6", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ width: "40px", height: "40px", border: "3px solid #E5E7EB", borderTop: "3px solid #C9A84C", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 16px" }} />
          <p style={{ color: "#6B7280", fontSize: "14px" }}>Loading admin dashboard…</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  const s = stats;

  return (
    <div style={{ minHeight: "100vh", background: "#F3F4F6", fontFamily: "Inter, sans-serif" }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @media (max-width: 768px) {
          .admin-stat-grid  { grid-template-columns: 1fr 1fr !important; }
          .admin-chart-row  { flex-direction: column !important; }
          .admin-recent-row { flex-direction: column !important; }
          .admin-storage-row { flex-direction: column !important; }
          .admin-users-table { overflow-x: auto !important; }
        }
      `}</style>

      {/* ── TOP BAR ── */}
      <div style={{ background: "#1F2937", padding: "0 24px", height: "60px", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100, boxShadow: "0 2px 8px rgba(0,0,0,0.3)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <a href="/" style={{ color: "#C9A84C", fontWeight: 800, fontSize: "18px", textDecoration: "none" }}>TaxThinker</a>
          <span style={{ background: "#DC2626", color: "white", fontSize: "11px", fontWeight: 700, padding: "2px 8px", borderRadius: "99px", letterSpacing: "0.5px" }}>ADMIN</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          {lastRefresh && (
            <span style={{ color: "#9CA3AF", fontSize: "12px" }}>
              Updated {lastRefresh.toLocaleTimeString()}
            </span>
          )}
          <button
            onClick={fetchStats}
            disabled={loading}
            style={{ background: "#374151", color: "#D1D5DB", border: "1px solid #4B5563", borderRadius: "8px", padding: "6px 14px", fontSize: "13px", cursor: "pointer", fontWeight: 600 }}
          >
            {loading ? "…" : "↻ Refresh"}
          </button>
          <button
            onClick={handleLogout}
            style={{ background: "transparent", color: "#EF4444", border: "1px solid #EF4444", borderRadius: "8px", padding: "6px 14px", fontSize: "13px", cursor: "pointer", fontWeight: 600 }}
          >
            Logout
          </button>
        </div>
      </div>

      {error && (
        <div style={{ background: "#FEE2E2", color: "#9C0006", padding: "12px 24px", fontSize: "13px", borderBottom: "1px solid #FECACA" }}>
          Error: {error}
        </div>
      )}

      <div style={{ maxWidth: "1400px", margin: "0 auto", padding: "24px" }}>

        {/* ── SECTION 2: KEY METRICS ── */}
        <div
          className="admin-stat-grid"
          style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: "16px", marginBottom: "24px" }}
        >
          <StatCard
            icon="👥" title="Total Users"
            value={s?.users?.total ?? "—"}
            subtext={`+${s?.users?.this_month ?? 0} this month`}
            color="#3B82F6"
          />
          <StatCard
            icon="📊" title="Reports Generated"
            value={s?.reports?.total_all ?? "—"}
            subtext={`+${(s?.reports?.capital_gain?.this_month ?? 0) + (s?.reports?.bank_statement?.this_month ?? 0)} this month`}
            color="#10B981"
          />
          <StatCard
            icon="📈" title="Capital Gain Reports"
            value={s?.reports?.capital_gain?.total ?? "—"}
            subtext={`${s?.reports?.capital_gain?.today ?? 0} today`}
            color="#C9A84C"
          />
          <StatCard
            icon="🏦" title="Bank Statements"
            value={s?.reports?.bank_statement?.total ?? "—"}
            subtext={`+${s?.reports?.bank_statement?.this_month ?? 0} this month`}
            color="#8B5CF6"
          />
          <StatCard
            icon="💰" title="Revenue (MRR)"
            value="Pending"
            subtext="Razorpay integration coming"
            color="#6B7280"
            dimmed
          />
          <StatCard
            icon="💾" title="Storage Used"
            value={s?.storage?.total_gb != null ? `${s.storage.total_gb} GB` : "—"}
            subtext={`${s?.storage?.total_files ?? 0} files`}
            color="#F97316"
          />
        </div>

        {/* ── SECTION 3: SERVER HEALTH ── */}
        {s?.server && !s.server.error && (
          <div style={{ ...card, marginBottom: "24px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", flexWrap: "wrap", gap: "8px" }}>
              <h2 style={sectionTitle}>Server Health</h2>
              <div style={{ display: "flex", gap: "24px", fontSize: "12px", color: "#6B7280", flexWrap: "wrap" }}>
                <span>EC2: t3.medium</span>
                <span>{s.server.cpu_count} vCPUs</span>
                <span>{s.server.memory_total_gb} GB RAM</span>
                <span style={{ color: s.server.cpu_percent < 50 ? "#375623" : s.server.cpu_percent < 70 ? "#C9A84C" : "#9C0006", fontWeight: 700 }}>
                  {s.server.cpu_percent < 50 ? "High capacity" : s.server.cpu_percent < 70 ? "Moderate capacity" : "Approaching limits"}
                </span>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px" }}>
              <div>
                <ProgressBar label="CPU Usage" value={s.server.cpu_percent} />
                <ProgressBar label="Memory Usage" value={s.server.memory_percent} />
              </div>
              <div>
                <ProgressBar label="Disk Usage" value={s.server.disk_percent} />
                <div style={{ fontSize: "12px", color: "#9CA3AF", marginTop: "8px" }}>
                  {s.server.disk_free_gb} GB free of {s.server.disk_total_gb} GB total
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── SECTION 4: CHARTS ── */}
        {s && (
          <div className="admin-chart-row" style={{ display: "flex", gap: "16px", marginBottom: "24px" }}>
            <div style={{ ...card, flex: 1 }}>
              {s.users?.monthly_chart?.length > 0
                ? <BarChart data={s.users.monthly_chart} label="New Users Per Month" color="#3B82F6" />
                : <p style={{ color: "#9CA3AF", fontSize: "13px", padding: "16px" }}>No signup data yet</p>
              }
            </div>
            <div style={{ ...card, flex: 1 }}>
              {s.reports?.capital_gain?.monthly_chart?.length > 0
                ? <BarChart data={s.reports.capital_gain.monthly_chart} label="Capital Gain Reports Per Month" color="#C9A84C" />
                : <p style={{ color: "#9CA3AF", fontSize: "13px", padding: "16px" }}>No report data yet</p>
              }
            </div>
          </div>
        )}

        {/* ── SECTION 5: STORAGE ── */}
        {s?.storage && !s.storage.error && (
          <div style={{ ...card, marginBottom: "24px" }}>
            <h2 style={sectionTitle}>S3 Storage</h2>
            {(["reports_bucket", "uploads_bucket", "textract_bucket"] as const).map((key) => {
              const bucket = s.storage[key];
              const labels: Record<string, string> = {
                reports_bucket: "Reports Bucket",
                uploads_bucket: "Uploads Bucket",
                textract_bucket: "Textract Bucket",
              };
              const maxMb = Math.max(
                s.storage.reports_bucket.size_mb,
                s.storage.uploads_bucket.size_mb,
                s.storage.textract_bucket.size_mb,
                1
              );
              return (
                <div key={key} style={{ marginBottom: "12px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", marginBottom: "4px" }}>
                    <span style={{ fontWeight: 600, color: "#374151" }}>{labels[key]}</span>
                    <span style={{ color: "#6B7280" }}>{bucket.size_gb} GB ({bucket.count} files)</span>
                  </div>
                  <div style={{ height: "6px", background: "#EAE6E0", borderRadius: "3px", overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${(bucket.size_mb / maxMb) * 100}%`, background: "#F97316", borderRadius: "3px" }} />
                  </div>
                </div>
              );
            })}
            <div style={{ borderTop: "1px solid #E5E7EB", marginTop: "12px", paddingTop: "10px", display: "flex", justifyContent: "space-between", fontSize: "13px", fontWeight: 700, color: "#374151" }}>
              <span>Total</span>
              <span>{s.storage.total_gb} GB ({s.storage.total_files} files)</span>
            </div>
          </div>
        )}

        {/* ── SECTION 6: RECENT ACTIVITY ── */}
        {s?.recent && !s.recent.error && (
          <div className="admin-recent-row" style={{ display: "flex", gap: "16px", marginBottom: "24px" }}>
            {/* Recent Signups */}
            <div style={{ ...card, flex: 1, overflow: "hidden" }}>
              <h2 style={sectionTitle}>Recent Signups</h2>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                <thead>
                  <tr style={{ borderBottom: "1.5px solid #E5E7EB" }}>
                    {["Name", "Email", "Plan", "Joined"].map(h => (
                      <th key={h} style={{ textAlign: "left", padding: "6px 8px", color: "#6B7280", fontWeight: 600, fontSize: "11px", textTransform: "uppercase" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {s.recent.users.map((u, i) => (
                    <tr key={i} style={{ borderBottom: "1px solid #F3F4F6" }}>
                      <td style={{ padding: "7px 8px", fontWeight: 600, color: "#1f2937" }}>{u.name}</td>
                      <td style={{ padding: "7px 8px", color: "#6B7280", maxWidth: "160px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.email}</td>
                      <td style={{ padding: "7px 8px" }}><PlanBadge plan={u.plan} /></td>
                      <td style={{ padding: "7px 8px", color: "#9CA3AF", whiteSpace: "nowrap" }}>{u.joined}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Recent Reports */}
            <div style={{ ...card, flex: 1, overflow: "hidden" }}>
              <h2 style={sectionTitle}>Recent Reports</h2>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                <thead>
                  <tr style={{ borderBottom: "1.5px solid #E5E7EB" }}>
                    {["Client", "Type", "Date"].map(h => (
                      <th key={h} style={{ textAlign: "left", padding: "6px 8px", color: "#6B7280", fontWeight: 600, fontSize: "11px", textTransform: "uppercase" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    ...s.recent.capital_gain.map(r => ({ client: r.client, type: "CG", icon: "📈", detail: r.ay, date: r.date })),
                    ...s.recent.bank_statement.map(r => ({ client: r.client, type: "BS", icon: "🏦", detail: r.bank, date: r.date })),
                  ]
                    .sort((a, b) => (b.date > a.date ? 1 : -1))
                    .slice(0, 10)
                    .map((r, i) => (
                      <tr key={i} style={{ borderBottom: "1px solid #F3F4F6" }}>
                        <td style={{ padding: "7px 8px", fontWeight: 600, color: "#1f2937" }}>{r.client}</td>
                        <td style={{ padding: "7px 8px" }}>
                          <span style={{ fontSize: "16px", marginRight: "4px" }}>{r.icon}</span>
                          <span style={{ fontSize: "11px", color: "#6B7280" }}>{r.detail}</span>
                        </td>
                        <td style={{ padding: "7px 8px", color: "#9CA3AF", whiteSpace: "nowrap" }}>{r.date}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── SECTION 7: ALL USERS TABLE ── */}
        <div style={{ ...card, marginBottom: "24px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", flexWrap: "wrap", gap: "12px" }}>
            <h2 style={{ ...sectionTitle, marginBottom: 0 }}>All Users</h2>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <span style={{ fontSize: "13px", color: "#6B7280" }}>
                Showing {Math.min((usersPage - 1) * 20 + 1, usersTotal)}–{Math.min(usersPage * 20, usersTotal)} of {usersTotal}
              </span>
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search name or email…"
                style={{ border: "1.5px solid #E5E7EB", borderRadius: "8px", padding: "6px 12px", fontSize: "13px", outline: "none", width: "220px" }}
              />
            </div>
          </div>

          <div className="admin-users-table" style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
              <thead>
                <tr style={{ borderBottom: "1.5px solid #E5E7EB" }}>
                  {["Name", "Email", "Plan", "Login", "Country", "Joined"].map(h => (
                    <th key={h} style={{ textAlign: "left", padding: "8px 10px", color: "#6B7280", fontWeight: 600, fontSize: "11px", textTransform: "uppercase", whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {usersLoading ? (
                  <tr><td colSpan={6} style={{ padding: "24px", textAlign: "center", color: "#9CA3AF" }}>Loading…</td></tr>
                ) : users.length === 0 ? (
                  <tr><td colSpan={6} style={{ padding: "24px", textAlign: "center", color: "#9CA3AF" }}>No users found</td></tr>
                ) : users.map(u => (
                  <tr key={u.id} style={{ borderBottom: "1px solid #F3F4F6" }}>
                    <td style={{ padding: "8px 10px", fontWeight: 600, color: "#1f2937", whiteSpace: "nowrap" }}>{u.name}</td>
                    <td style={{ padding: "8px 10px", color: "#6B7280", maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.email}</td>
                    <td style={{ padding: "8px 10px" }}><PlanBadge plan={u.plan} /></td>
                    <td style={{ padding: "8px 10px", color: "#6B7280" }}>{u.login_type}</td>
                    <td style={{ padding: "8px 10px", color: "#6B7280", whiteSpace: "nowrap" }}>{u.country}</td>
                    <td style={{ padding: "8px 10px", color: "#9CA3AF", whiteSpace: "nowrap" }}>{u.joined}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "8px", marginTop: "16px" }}>
            <button
              onClick={() => setUsersPage(p => Math.max(1, p - 1))}
              disabled={usersPage <= 1 || usersLoading}
              style={{ padding: "6px 14px", borderRadius: "8px", border: "1.5px solid #E5E7EB", background: usersPage <= 1 ? "#F3F4F6" : "white", color: usersPage <= 1 ? "#9CA3AF" : "#374151", cursor: usersPage <= 1 ? "not-allowed" : "pointer", fontSize: "13px", fontWeight: 600 }}
            >
              ← Prev
            </button>
            <span style={{ fontSize: "13px", color: "#6B7280" }}>Page {usersPage} of {usersTotalPages}</span>
            <button
              onClick={() => setUsersPage(p => Math.min(usersTotalPages, p + 1))}
              disabled={usersPage >= usersTotalPages || usersLoading}
              style={{ padding: "6px 14px", borderRadius: "8px", border: "1.5px solid #E5E7EB", background: usersPage >= usersTotalPages ? "#F3F4F6" : "white", color: usersPage >= usersTotalPages ? "#9CA3AF" : "#374151", cursor: usersPage >= usersTotalPages ? "not-allowed" : "pointer", fontSize: "13px", fontWeight: 600 }}
            >
              Next →
            </button>
          </div>
        </div>

        {/* ── SECTION 8: REVENUE PLACEHOLDER ── */}
        <div style={{ ...card, background: "#F9FAFB", marginBottom: "24px" }}>
          <h2 style={sectionTitle}>💰 Revenue Analytics</h2>
          <p style={{ fontSize: "14px", color: "#6B7280", marginBottom: "16px" }}>
            Razorpay integration is pending. Once connected, this section will show:
          </p>
          <div style={{ filter: "blur(3px)", pointerEvents: "none", userSelect: "none", display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: "12px", marginBottom: "16px" }}>
            {[
              ["MRR", "₹4,999"], ["Total Revenue", "₹49,999"], ["Paying Customers", "23"],
              ["ARPU", "₹2,174"], ["Churn Rate", "2.1%"],
            ].map(([label, val]) => (
              <div key={label} style={{ background: "white", border: "1.5px solid #E5E7EB", borderRadius: "10px", padding: "14px" }}>
                <div style={{ fontSize: "20px", fontWeight: 800, color: "#1f2937" }}>{val}</div>
                <div style={{ fontSize: "12px", color: "#6B7280", marginTop: "4px" }}>{label}</div>
              </div>
            ))}
          </div>
          <a
            href="/pricing"
            style={{ display: "inline-block", padding: "8px 20px", background: "#C9A84C", color: "#2C2C2C", borderRadius: "8px", fontWeight: 700, fontSize: "13px", textDecoration: "none" }}
          >
            Setup Razorpay →
          </a>
        </div>

      </div>
    </div>
  );
}
